import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...classes: (string | undefined | null | false)[]): string {
  return twMerge(clsx(classes));
}

/**
 * Type guard to check if an error is a CanceledError
 */
export function isCanceledError(error: unknown): error is { name: string; code?: string } {
  return (
    error !== null &&
    typeof error === 'object' &&
    ('name' in error || 'code' in error) &&
    ((error as { name?: string; code?: string }).name === 'CanceledError' ||
      (error as { name?: string; code?: string }).code === 'ERR_CANCELED')
  );
}

const THROTTLE_DEFAULT_LIMIT = 33; // ~30fps

/**
 * Throttles a function to execute at most once per specified time limit.
 * Ensures the last call is always executed via the flush method.
 */
export function throttle<T extends (...args: never[]) => void>(
  func: T,
  limit: number = THROTTLE_DEFAULT_LIMIT
): T & { flush: () => void } {
  let inThrottle = false;
  let lastArgs: Parameters<T> | null = null;

  const throttled = function (...args: Parameters<T>) {
    lastArgs = args;
    if (!inThrottle) {
      func(...args);
      lastArgs = null;
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
        if (lastArgs) {
          func(...lastArgs);
          lastArgs = null;
        }
      }, limit);
    }
  } as T & { flush: () => void };

  throttled.flush = function () {
    if (lastArgs) {
      func(...lastArgs);
      lastArgs = null;
    }
  };

  return throttled;
}
